import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useEffect, useState } from "react";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { UserCommunicationTypeListItemDto } from "../types";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import { useModal } from "../../../../hooks/useModal";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { activeData, deleteData } from "../../Dictionaries/api/basicGenericTable";
import { Actions } from "../../Dictionaries/types";
import { useAlert } from "../../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useUserCommunicationTypes } from "../hooks/useUserCommunicationTypes";
import { exportCsvUserCommunicationType } from "../api/userCommunicationTypeApi";

export default function UserCommunicationTypeList() {
    const { t, i18n } = useTranslation(["common", "userCommunicationType"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const tableName = "userCommunicationTypes";

    const currentPage = useSelector((state: RootState) => state.historical.improvementPlans.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.improvementPlans.itemsPerPage);
    const onlyActiveToggleChange = useSelector((state: RootState) => state.filters.userCommunicationTypes.filterByOnlyActiveRecords);
    
    const [messageError, setMessageError] = useState<string | undefined>("");
    const {showModal, modalData, errorModal, endDate, openModal: openDeleteWithoutImputModal, openModal: openActivateModal, closeModal, setLoading,
        isLoading: isModalLoading,
        currentItem
    } = useModal<UserCommunicationTypeListItemDto>();

    const { data: userCommunicationTypePagination, isLoading, refetch } = useUserCommunicationTypes({});

    const genericActions: Actions = {
        onDelete: async (id: number, table: string) => await deleteData(id, table),
        onActive: async (id: number, table: string) => await activeData(id, table),
        onView: (id: number) => navigate(`/admin/${tableName}/${id}`),
        onEdit: (id: number) => navigate(`/admin/${tableName}/${id}`)
    };
    
    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const { showAlert } = useAlert({ fromLocation: true });

    const handleFilterByActives = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByOnlyActiveRecords({ table: 'userCommunicationTypes', filterByOnlyActiveRecords: e.target.checked }));
    };

    const goToCreate = () => {
        navigate(`/admin/userCommunicationTypes/new`);
    };

    const handleDelete = async (genericList: UserCommunicationTypeListItemDto) => {
        openDeleteWithoutImputModal(
            genericList,
            t("userCommunicationType:modal.deleteTitle"),
            `${t("userCommunicationType:modal.deleteText", { description: getTranslatedDescription(genericList.description, i18n.language) })}`,
            "deleteWithoutInput",
            ""
        );
    };

    const handleActive = (genericList: UserCommunicationTypeListItemDto) => {
        openActivateModal(
            genericList,
            t("userCommunicationType:modal.activeTitle"),
            `${t("userCommunicationType:modal.activeText", { description: getTranslatedDescription(genericList.description, i18n.language) })}`,
            "active",
            ""
        );
    };

    const confirmSaveModal = async () => {
        setLoading(true);

        try {
            if (modalData.type === "deleteWithoutInput") {
                await genericActions.onDelete(currentItem!.id, tableName);
                refetch();
            }

            if (modalData.type === "active") {
                await genericActions.onActive(currentItem!.id, tableName);
                refetch();
            }
        } catch (err) {
            showAlert({
                message: (err as ApiErrorResponse).message || t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000
            });
        } finally {
            closeModal();
            setLoading(false);
        }
    }
    
    const handleExportCsv = () => {
        exportCsvUserCommunicationType()
            .then(() => {
                showAlert({
                    message: t("Success"),
                    type: "success",
                    duration: 8000
                });
            })
            .catch((err: ApiErrorResponse) => {
                showAlert({
                    message: err.message || t("common:requestError.errorBadRequest"),
                    type: "danger",
                    duration: 8000
                });
            })
            .finally(() => closeModal());
    };

    const columns: ColumnConfig<UserCommunicationTypeListItemDto>[] = [
        { header: t("common:form.fields.id"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("common:form.fields.description"), key: "description", render: (item) => <>{getTranslatedDescription(item.description, i18n.language)}</> },
        { header: t("userCommunicationType:table.updateResponseDate"), key: "canUpdateResponseDate", render: (item) => <>{item.canUpdateResponseDate ? t("common:yes") : t("common:no")}</> },
        { header: t("common:form.fields.status"), key: "status", render: (item) => <>{item.isActive ? t("common:stateActive") : t("common:stateInactive")}</> },
    ];

    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                endDate={endDate}
                onSave={confirmSaveModal}
                isLoading={isModalLoading}
            />
            
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("userCommunicationType:title")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        className="text-nowrap"
                        onClick={goToCreate}
                    >
                        {t("userCommunicationType:buttonNew")}
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {messageError && (
                        <div className="alert alert-danger">
                            {messageError}
                            <button
                                type="button"
                                className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                                aria-label="Close"
                                onClick={() => setMessageError(undefined)}
                            ></button>
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleFilterByActives}
                        checked={onlyActiveToggleChange}
                    />
                </Form>

            </Row>
            <AppTable<UserCommunicationTypeListItemDto>
                data={userCommunicationTypePagination?.items || []}
                columns={columns}
                emptyMessage={t("userCommunicationType:emptyMessage")}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={userCommunicationTypePagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="userCommunicationTypes"
                sectionPermission={SectionPermissionTypeEnum.USER_COMMUNICATION_TYPE}
                onView={genericActions.onView}
                onExportCsv={handleExportCsv}
                onDelete={handleDelete}
                onActive={handleActive}
            />

            {isLoading && (
                <Row>
                    <Col xs={12} className="text-center mt-2">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            )}
        </>
    );
}
